import React, { useState } from "react";
import { connect } from "react-redux";
import { Alert, Badge, Button, Card } from "react-bootstrap";
import { Home, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QRScanner from "../components/QRScanner";
import SearchableSelect from "../components/utilities/SearchableSelectBox";
import { IAsset } from "../models/IAsset";
import { Option } from "../models/types";
import ApiClient from "../utilities/http";
import { Endpoints } from "../utilities/endpoints";
import { toastError, toastSuccess } from "../actions/formInputAction";

interface Props {
    dispatch: Function;
}

interface ScannedAsset {
    id: string;
    identifier: string;
    title: string;
}

interface AssigneeData {
    id: string;
    name: string;
}

type AssigneeMap = Record<string, AssigneeData | null>;

const decodeAssetScan = (data: string): string | null => {
    try {
        const url = new URL(data);
        return url.searchParams.get("asset");
    } catch (_err) {
        return data;
    }
};

const AssetAssignment: React.FC<Props> = ({ dispatch }) => {
    const { pathname } = useLocation();
    const mode: "assign" | "return" = pathname.endsWith("/return") ? "return" : "assign";
    const isAssign = mode === "assign";
    const [step, setStep] = useState<"scan" | "user">("scan");
    const [assets, setAssets] = useState<ScannedAsset[]>([]);
    const [assignees, setAssignees] = useState<AssigneeMap>({});
    const [selectedUser, setSelectedUser] = useState<Option | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const nav = useNavigate();
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string, options?: any) => i18n.t(txt, options);

    const title = isAssign ? t("asset_assignment.assign") : t("asset_assignment.return");

    const addAsset = (asset: IAsset) => {
        setAssets((prev) => {
            if (prev.some((record) => record.id === asset.id)) {
                return prev;
            }

            return [
                ...prev,
                {
                    id: asset.id,
                    identifier: asset.identifier,
                    title: asset.name || asset.identifier || asset.id,
                },
            ];
        });
    };

    const removeAsset = (assetId: string) => {
        setAssets((prev) => prev.filter((asset) => asset.id !== assetId));
        setAssignees((prev) => {
            const next = { ...prev };
            delete next[assetId];
            return next;
        });
    };

    const loadCurrentAssignee = async (assetId: string) => {
        try {
            const response = await ApiClient.get(`${Endpoints.USER_ASSET_ASSIGNMENT}/assignee/${assetId}`);
            setAssignees((prev) => ({
                ...prev,
                [assetId]: response.data?.user || null,
            }));
        } catch (_err) {
            setAssignees((prev) => ({
                ...prev,
                [assetId]: null,
            }));
        }
    };

    const onScanResult = async (rawData: string) => {
        const scannedValue = decodeAssetScan(rawData);
        if (!scannedValue) {
            return;
        }

        try {
            const response = await ApiClient.get(`${Endpoints.ASSET}?code=${encodeURIComponent(scannedValue)}`);
            const asset: IAsset | undefined = response.data?.asset_item;
            if (!asset) {
                dispatch(toastError({}, t("asset_assignment.unknown_scanned_asset_title"), t("asset_assignment.unknown_scanned_asset_message")));
                return;
            }

            addAsset(asset);
            loadCurrentAssignee(asset.id);
        } catch (error) {
            dispatch(toastError(error, t("asset_assignment.error_fetching_asset_title"), t("asset_assignment.error_fetching_asset_message")));
        }
    };

    const submit = async () => {
        if (assets.length === 0) {
            dispatch(toastError({}, t("asset_assignment.no_assets_selected_title"), t("asset_assignment.no_assets_selected_message")));
            return;
        }
        if (isAssign && !selectedUser?.value) {
            dispatch(toastError({}, t("asset_assignment.no_user_selected_title"), t("asset_assignment.no_user_selected_message")));
            return;
        }

        setIsSaving(true);
        try {
            await ApiClient.post(Endpoints.USER_ASSET_ASSIGNMENT, {
                action: mode,
                user_id: isAssign ? selectedUser?.value : null,
                asset_ids: assets.map((asset) => asset.id),
            });

            dispatch(toastSuccess(t("success"), isAssign ? t("asset_assignment.assets_assigned_success") : t("asset_assignment.assets_returned_success")));
            setAssets([]);
            setAssignees({});
            setSelectedUser(null);
            setStep("scan");
        } catch (error) {
            dispatch(toastError(error, t("asset_assignment.submission_failed_title"), t("asset_assignment.submission_failed_message")));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div style={{ marginBottom: -10 }}>
                <Home style={{ marginTop: "-10px", color: "#0e6efd" }} onClick={() => nav("/dashboard")} />
                <span style={{ marginLeft: 5, fontSize: 20 }}>≫</span>
                <span
                    style={{
                        marginBottom: "10px",
                        marginRight: "10px",
                        fontSize: "18px",
                        fontWeight: "bolder",
                        padding: "5px",
                    }}
                >
                    {title}
                </span>
            </div>

            {step === "scan" && (
                <>
                    <QRScanner onResult={onScanResult} scanOnload={false} searchController="asset" />
                    <Card className="mt-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>{isAssign ? t("asset_assignment.selected_assets") : t("asset_assignment.assets_to_return")}</strong>
                                <Badge bg="secondary">{assets.length}</Badge>
                            </div>
                            {assets.length === 0 ? (
                                <Alert variant="light" className="mb-0">
                                    {t("asset_assignment.scan_or_search_assets")}
                                </Alert>
                            ) : (
                                <div className="d-grid gap-2">
                                    {assets.map((asset) => (
                                        <div key={asset.id} className="border rounded p-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>{asset.title}</div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger py-0 px-1"
                                                    onClick={() => removeAsset(asset.id)}
                                                    aria-label={t("asset_assignment.remove_asset_aria", { asset: asset.title })}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                            <small className="text-muted">{t("asset_assignment.asset_id_label")}: {asset.identifier || asset.id}</small>
                                            <div className="mt-1">
                                                <small className={isAssign && assignees[asset.id]?.name ? "text-danger" : "text-muted"}>
                                                    {isAssign
                                                        ? t("asset_assignment.already_assigned_to_label")
                                                        : t("asset_assignment.assigned_to_label")}: {assignees[asset.id]?.name || t("asset_assignment.unassigned")}
                                                </small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                    <div className="d-flex gap-2 mt-3">
                        {isAssign ? (
                            <Button disabled={assets.length === 0} onClick={() => setStep("user")} style={{width: '100%'}}>
                                {t("asset_assignment.next")}
                            </Button>
                        ) : (
                            <Button disabled={assets.length === 0 || isSaving} onClick={submit} style={{width: '100%'}}>
                                {t("asset_assignment.return")}
                            </Button>
                        )}
                    </div>
                </>
            )}

            {isAssign && step === "user" && (
                <Card className="mt-3">
                    <Card.Body>
                        <h5>{t("asset_assignment.select_user")}</h5>
                        <SearchableSelect
                            controller="user"
                            name="assignment_user"
                            label={t("asset_assignment.user")}
                            value={selectedUser}
                            onSelect={(option: Option | null) => setSelectedUser(option)}
                        />
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="outline-secondary" onClick={() => setStep("scan")}  style={{width: '49%'}}>
                                {t("asset_assignment.back")}
                            </Button>
                            <Button disabled={!selectedUser?.value || isSaving} onClick={submit}  style={{width: '49%'}}>
                                {t("asset_assignment.assign")}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default connect()(AssetAssignment);
